import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { IMAGES } from "@/data/content";
import { Eyebrow, Reveal } from "./Reveal";

const TILES = [
  { src: IMAGES.croissants, caption: "Croissants feuilletés, chaque matin", span: "col-span-2 row-span-2" },
  { src: IMAGES.terrace, caption: "La terrasse, place des Lices", span: "col-span-2 row-span-1" },
  { src: IMAGES.entrance, caption: "L'entrée fleurie", span: "col-span-1 row-span-2" },
  { src: IMAGES.latte, caption: "Latte art du comptoir", span: "col-span-1 row-span-2" },
  { src: IMAGES.breakfast, caption: "Petit-déjeuner au soleil", span: "col-span-2 row-span-1" },
  { src: IMAGES.pastryTray, caption: "Le plateau du fournil", span: "col-span-2 row-span-1" },
  { src: IMAGES.pie, caption: "Pause café & tarte", span: "col-span-2 row-span-1" },
];

export const Gallery = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="galerie" data-testid="gallery-section" className="bg-[#FAF7F2] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <Eyebrow>En images</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="gallery-title"
                className="mt-6 font-display text-3xl font-semibold tracking-tight text-[#1B3B2B] sm:text-4xl lg:text-5xl"
              >
                L'atmosphère
                <br />
                des <em className="italic text-[#C85A32]">Arcades</em>.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xs text-sm font-light leading-relaxed text-[#3D5A4B]/85">
              Un comptoir en chêne, la lumière du matin, l'odeur du beurre
              chaud — bienvenue chez nous.
            </p>
          </Reveal>
        </div>

        <div
          data-testid="gallery-grid"
          className="mt-14 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[190px] md:grid-cols-4 md:gap-4"
        >
          {TILES.map((tile, i) => (
            <Reveal key={tile.caption} delay={0.06 * i} className={tile.span}>
              <button
                type="button"
                data-testid={`gallery-tile-${i}`}
                onClick={() => setSelected(tile)}
                className="group relative block h-full w-full overflow-hidden rounded-2xl"
              >
                <img
                  src={tile.src}
                  alt={tile.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-[#12281D]/0 transition-colors duration-500 group-hover:bg-[#12281D]/35" />
                <span className="absolute inset-x-0 bottom-0 translate-y-3 p-4 text-left font-display text-base italic text-[#F5F0E6] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100 sm:text-lg">
                  {tile.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#12281D]/90 p-5 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.src}
                alt={selected.caption}
                className="max-h-[76vh] w-full rounded-2xl object-contain"
              />
              <figcaption className="mt-4 text-center font-display text-lg italic text-[#F5F0E6]">
                {selected.caption}
              </figcaption>
              <button
                type="button"
                data-testid="gallery-lightbox-close"
                onClick={() => setSelected(null)}
                aria-label="Fermer"
                className="absolute -right-3 -top-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#C85A32] text-[#FAF7F2] transition-colors hover:bg-[#A84420]"
              >
                <X size={18} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
